import { useCallback, useRef, useState } from 'react'
import { useErrorHandler } from 'react-error-boundary'
import { useNavigate } from 'react-router-dom'
import type { Pagination } from '../../interfaces/index'
import type { AppAPIClient } from '../../lib'
import { transformBeePerkToSearchResult } from '../../lib/utils/transformPerk'
import type { SearchPerkResult } from '../../types'

const usePerksModal = (
  _pagination: Pagination,
  _availableCategories: string[] | null,
  api: AppAPIClient,
) => {
  const navigate = useNavigate()
  const savedSearch = useRef('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalPerk, setModalPerk] = useState<SearchPerkResult | null>(null)
  const handleError = useErrorHandler()

  // Shared function to load and display a perk by ID
  const loadAndShowPerk = useCallback(
    async (
      perkId: string,
      cachedPerk?: SearchPerkResult,
    ): Promise<boolean> => {
      try {
        savedSearch.current = window.location.search
        const { data } = await api.v1_perks.getOne(perkId)

        if (data) {
          const transformedPerk = transformBeePerkToSearchResult(data)
          setModalPerk(transformedPerk)
          setIsModalOpen(true)
          navigate(`/perk/${perkId}${savedSearch.current}`, { replace: false })
          return true
        }
      } catch (error) {

        if (cachedPerk) {
          setModalPerk(cachedPerk)
          setIsModalOpen(true)
          navigate(`/perk/${perkId}${savedSearch.current}`, { replace: false })
          return true
        }

        handleError(error)
      }

      return false
    },
    [api, handleError, navigate],
  )

  const toggleModal = useCallback(
    async (perk?: SearchPerkResult) => {
      if (perk) {
        await loadAndShowPerk(perk.id, perk)
      } else {
        setIsModalOpen(false)
        setModalPerk(null)
        navigate(`/${savedSearch.current}`, { replace: false })
      }
    },
    [loadAndShowPerk, navigate],
  )

  const openModalForPerkId = useCallback(
    async (perkId: string): Promise<boolean> => {
      return loadAndShowPerk(perkId)
    },
    [loadAndShowPerk],
  )

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
    setModalPerk(null)
    navigate(`/${savedSearch.current}`, { replace: false })
  }, [navigate])

  return {
    isModalOpen,
    modalPerk,
    toggleModal,
    openModalForPerkId,
    closeModal,
  }
}

export default usePerksModal
